import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import noop from 'lib/function/noop'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Select from 'components/Common/Form/Input/Select'
import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import DatePickerDay from 'components/Common/Calendar/DatePickerDay'
import TimeInput from 'components/Common/Form/Input/TimeInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import AddressInput from 'components/Common/Form/Input/AddressInput/AddressInput'
import ExperienceDetailsTransferBookingTravellers from 'components/Experiences/ExperienceTransfers/ExperienceTransferBookingTravellers'
import ExperienceDetailsTransferBookingBaggage from 'components/Experiences/ExperienceTransfers/ExperienceTransferBookingBaggage'
import { formatTransferBaggage, formatTransferTravellers } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import type { GoogleAutocompletePlaceItem } from 'api/search'
import { getAutocompleteSuggestions } from 'api/search'
import { addDays, startOfDay } from 'lib/datetime/dateUtils'
import useHomeToAirportTransferData from 'checkout/hooks/useHomeToAirportTransferData'
import useTransferPricing, { MAX_DISTANCE_KM } from 'checkout/hooks/loyalty/useTransferPricing'
import TransferSurchargeSummary from './TransferSurchargeSummary'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { reportClientError } from 'services/errorReportingService'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { findRedemptionLocationByAirportId } from 'lib/experiences/redemptionLocationUtils'

const StretchedGroup = styled(Group)`
  > * {
    flex: 1;
  }
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

export interface HomeToAirportSubmitData {
  transfer: App.ExperienceTransferView
  experience: App.ExperienceOffer
}

interface Props {
  formId: string
  /** Initial values when editing an existing transfer */
  initialData?: {
    pickUpPlaceId?: string
    pickUpPlaceName?: string
    dropOffAirportCode?: string
    date?: string
    time?: string
    flightNumber?: string
  }
  /** Called when form is submitted with valid data */
  onSubmit: (data: HomeToAirportSubmitData) => void
  /** Called when distance exceeds maximum allowed */
  onDistanceExceedsMax?: (exceeds: boolean) => void
  /** Called when all required fields are filled (for submit button state) */
  onFormValidChange?: (isValid: boolean) => void
}

/**
 * Internal state for the transfer form.
 * Uses 'HOTEL-TO-AIRPORT' as the type since that's what the backend expects.
 * The 'hotel' field contains the home address - this is a semantic mismatch but
 * keeps the data structure consistent with regular transfers.
 */
interface TransferState {
  type: 'HOTEL-TO-AIRPORT'
  hotel: {
    name?: string
    googlePlaceId?: string
  }
  travellers: App.ExperienceTransferTravellers
  baggage: App.OrderTransferItemBaggage
}

function HomeToAirportTransferForm(props: Props) {
  const {
    formId,
    initialData,
    onSubmit,
    onDistanceExceedsMax = noop,
    onFormValidChange = noop,
  } = props

  const intl = useIntl()
  const defaultAirport = useDefaultAirport()

  const defaultAirportCode = defaultAirport?.code ?? ''

  // Core input states - all shown upfront
  const [selectedAirportId, setSelectedAirportId] = useState<string>(
    initialData?.dropOffAirportCode ?? '',
  )
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    initialData?.date,
  )
  const [selectedAddress, setSelectedAddress] = useState<{
    placeId?: string
    name?: string
    latitude?: number
    longitude?: number
    kmFromAirport?: number
  }>({
    placeId: initialData?.pickUpPlaceId,
    name: initialData?.pickUpPlaceName,
  })
  // Additional input states
  const [selectedTime, setSelectedTime] = useState<string | undefined>(initialData?.time)
  const [flightNumber, setFlightNumber] = useState<string>(initialData?.flightNumber ?? '')
  const [distanceCalculationError, setDistanceCalculationError] = useState(false)

  useEffect(() => {
    if (initialData?.dropOffAirportCode) return
    if (selectedAirportId) return
    if (!defaultAirportCode) return

    setSelectedAirportId(defaultAirportCode)
  }, [initialData?.dropOffAirportCode, selectedAirportId, defaultAirportCode])

  // Transfer state for travellers/baggage (reusing existing components)
  // Note: 'hotel' field contains the home address for this form
  const [transfer, setTransfer] = useState<TransferState>({
    type: 'HOTEL-TO-AIRPORT',
    hotel: {
      name: initialData?.pickUpPlaceName,
      googlePlaceId: initialData?.pickUpPlaceId,
    },
    travellers: { adults: 2, children: 0, childSeats: 0, boosterSeats: 0 },
    baggage: { bags: 2, oversizedBags: 0 },
  })

  // Fetch experience data, dates when airport is selected, time slots when airport + date are selected
  const homeToAirportData = useHomeToAirportTransferData(selectedAirportId, selectedDate)

  // Destructure commonly used values
  const { redemptionLocations, selectedRedemptionLocation } = homeToAirportData

  useEffect(() => {
    if (!redemptionLocations.length) return

    const currentSelection = findRedemptionLocationByAirportId(redemptionLocations, selectedAirportId)
    if (currentSelection) {
      if (currentSelection.id !== selectedAirportId) {
        setSelectedAirportId(currentSelection.id)
      }

      return
    }

    if (!selectedAirportId) return

    const preferredAirportId = initialData?.dropOffAirportCode ?? defaultAirportCode
    const preferredSelection = findRedemptionLocationByAirportId(redemptionLocations, preferredAirportId)
    const nextAirportId = preferredSelection?.id ?? redemptionLocations[0]?.id

    if (nextAirportId && nextAirportId !== selectedAirportId) {
      setSelectedAirportId(nextAirportId)
    }
  }, [defaultAirportCode, initialData?.dropOffAirportCode, redemptionLocations, selectedAirportId])

  // Get available dates from the API for the date picker
  const availableDates = homeToAirportData.availableDates

  // Create a Set of available date strings for quick lookup in the date picker
  const availableDateSet = useMemo(() => {
    return new Set(
      availableDates
        .filter(d => !d.soldOut)
        .map(d => d.day),
    )
  }, [availableDates])

  // Minimum date for date picker (tomorrow)
  const minDate = addDays(startOfDay(new Date()), 1)

  // Custom day render for date picker to only enable available dates
  const dayRender = useCallback((
    day: Date,
    params: { disabled: boolean, selected: boolean, onDayClick?: (day: Date) => void },
  ) => {
    const dateStr = moment(day).format(ISO_DATE_FORMAT)
    // Disable if the base disabled check fails OR if not in available dates (when we have them)
    const isUnavailable = availableDateSet.size > 0 && !availableDateSet.has(dateStr)
    const disabled = params.disabled || isUnavailable

    return (
      <DatePickerDay
        key={day.getTime()}
        selected={params.selected}
        date={day}
        disabled={disabled}
        onClick={params.onDayClick}
      />
    )
  }, [availableDateSet])

  // Get distance from address selection (single source of truth)
  const kmFromAirport = selectedAddress.kmFromAirport

  // Use memoized pricing hook for all charge calculations
  const {
    distanceExceedsMax,
    hasExtraDistanceCharge,
    extraDistanceCharge,
    hasCarSeatCharge,
    needsVehicleUpgrade,
    totalExtraCharge,
  } = useTransferPricing({
    kmFromAirport,
    travellers: transfer.travellers,
    baggage: transfer.baggage,
  })

  // Get available transfer options
  const transferOptions = homeToAirportData.transferOptions

  // Take the first transfer option directly (no filtering/validation)
  // This works with Societe transfers that have bookingOptions: null
  const firstTransferOption = useMemo(() => {
    return transferOptions.length > 0 ? transferOptions[0] : undefined
  }, [transferOptions])

  // Check if form has all required inputs filled
  const coreInputsFilled = !!(selectedRedemptionLocation && selectedDate && selectedAddress.placeId)
  const hasTicketValues = !!kmFromAirport
  const formIsValid = coreInputsFilled && hasTicketValues && !!firstTransferOption && !distanceExceedsMax

  // Notify parent of distance exceeds max
  useEffect(() => {
    onDistanceExceedsMax(distanceExceedsMax)
  }, [distanceExceedsMax, onDistanceExceedsMax])

  // Notify parent of form validity
  useEffect(() => {
    onFormValidChange(formIsValid)
  }, [formIsValid, onFormValidChange])

  // Calculate distance when both airport and address are present but distance is missing
  // This handles the case when address is entered before airport, or when airport changes
  useEffect(() => {
    let cancelled = false
    if (selectedRedemptionLocation && selectedAddress.name && !selectedAddress.kmFromAirport) {
      setDistanceCalculationError(false)
    }

    async function calculateDistance() {
      // Only calculate if we have both airport and address name, but no distance yet
      if (selectedRedemptionLocation && selectedAddress.name && !selectedAddress.kmFromAirport) {
        try {
          // Use autocomplete API with airport as origin to get distanceMeters
          const suggestions = await getAutocompleteSuggestions(selectedAddress.name, {
            origin: `${selectedRedemptionLocation.latitude},${selectedRedemptionLocation.longitude}`,
            location: `${selectedRedemptionLocation.latitude},${selectedRedemptionLocation.longitude}`,
          })
          if (cancelled) return
          // Find the matching place by placeId or use the first result
          const matchingPlace = selectedAddress.placeId
            ? suggestions.find(s => s.placeId === selectedAddress.placeId)
            : suggestions[0]

          if (!cancelled && matchingPlace?.distanceMeters) {
            const roundedKm = Math.ceil(matchingPlace.distanceMeters / 1000)
            setSelectedAddress(prev => ({ ...prev, kmFromAirport: roundedKm }))
            setDistanceCalculationError(false)
          } else if (!cancelled) {
            setDistanceCalculationError(true)
          }
        } catch(error) {
          if (!cancelled) {
            setDistanceCalculationError(true)
            reportClientError(error, {
              context: 'home_to_airport_transfer',
              action: 'calculate_distance',
              airportId: selectedAirportId,
              addressPlaceId: selectedAddress.placeId,
            })
          }
        }
      }
    }
    calculateDistance()

    return () => {
      cancelled = true
    }
  }, [selectedAirportId, selectedAddress.name, selectedAddress.placeId, selectedAddress.kmFromAirport, selectedRedemptionLocation])

  // Handle airport selection
  // Clear date when airport changes because available dates differ by airport
  // Keep address but clear kmFromAirport for recalculation
  const handleAirportChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAirportId(e.target.value)
    setSelectedDate(undefined)
    setDistanceCalculationError(false)
    setSelectedAddress(prev => ({
      ...prev,
      kmFromAirport: undefined,
    }))
  }, [])

  // Handle date selection
  const handleDateChange = useCallback((date?: Date) => {
    setSelectedDate(date ? moment(date).format('YYYY-MM-DD') : undefined)
  }, [])

  // Handle address selection from Google Places autocomplete
  // The autocomplete API returns distanceMeters from the origin airport (if airport is selected)
  // If no airport selected yet, we store without distance and calculate later
  const handleAddressChange = useCallback((place: GoogleAutocompletePlaceItem) => {
    setDistanceCalculationError(false)
    // Only use distanceMeters from autocomplete if an airport is already selected
    // (autocomplete biases results based on the airport coordinates)
    const distanceKm = selectedRedemptionLocation && place.distanceMeters
      ? Math.ceil(place.distanceMeters / 1000)
      : undefined
    const fullAddress = `${place.mainText}, ${place.secondaryText}`
    setSelectedAddress({
      placeId: place.placeId,
      name: fullAddress,
      kmFromAirport: distanceKm,
    })
    setTransfer(prev => ({
      ...prev,
      hotel: {
        ...prev.hotel,
        name: fullAddress,
        googlePlaceId: place.placeId,
      },
    }))
  }, [selectedRedemptionLocation])

  const handleTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTime(e.target.value)
  }, [])

  const handleFlightNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFlightNumber(e.target.value)
  }, [])

  const handleTravellersChange = useCallback((updatedTransfer: App.ExperienceTransferView) => {
    setTransfer(prev => ({
      ...prev,
      travellers: updatedTransfer.travellers,
    }))
  }, [])

  const handleBaggageChange = useCallback((updatedTransfer: App.ExperienceTransferView) => {
    setTransfer(prev => ({
      ...prev,
      baggage: updatedTransfer.baggage,
    }))
  }, [])

  const handleFormSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!firstTransferOption || !selectedRedemptionLocation || !homeToAirportData.experienceBookingView) {
      return
    }

    const airport: App.Airport = {
      code: selectedRedemptionLocation.state ?? '',
      name: selectedRedemptionLocation.city ?? selectedRedemptionLocation.name,
      latitude: selectedRedemptionLocation.latitude,
      longitude: selectedRedemptionLocation.longitude,
    }

    const specialRequests = [
      selectedAddress.name && intl.formatMessage(
        {
          id: 'qbwAkxzRuct5puXbl94k',
          defaultMessage: 'Pickup address: {address}',
          description: 'Special request summary line for the pickup address in the home-to-airport transfer form',
        },
        { address: selectedAddress.name },
      ),
      selectedTime && intl.formatMessage(
        {
          id: '89gIMJ07GgZRtmlVy-_V',
          defaultMessage: 'Flight departure: {time}',
          description: 'Special request summary line for the flight departure time in the home-to-airport transfer form',
        },
        { time: selectedTime },
      ),
      flightNumber && intl.formatMessage(
        {
          id: 'qX7DkLtnpCDHe3WwGkOm',
          defaultMessage: 'Flight: {flightNumber}',
          description: 'Special request summary line for the flight number in the home-to-airport transfer form',
        },
        { flightNumber },
      ),
      intl.formatMessage(
        {
          id: 'pdB_AixE5wnRUtXIHoIH',
          defaultMessage: 'Travellers: {travellers}',
          description: 'Special request summary line for traveller counts in the home-to-airport transfer form',
        },
        { travellers: formatTransferTravellers(transfer.travellers) },
      ),
      intl.formatMessage(
        {
          id: 'KBvzEFW5PKb8rC1yrl7_',
          defaultMessage: 'Bags: {bags}',
          description: 'Special request summary line for baggage counts in the home-to-airport transfer form',
        },
        { bags: formatTransferBaggage(transfer.baggage) },
      ),
    ].filter(Boolean).join('\n')

    // Note: For home-to-airport transfers, we use HOTEL-TO-AIRPORT as the backend type
    // and store the home address in the 'hotel' field. The isHomePickup flag identifies
    // this as a home pickup for UI purposes.
    const finalTransfer: App.ExperienceTransferView = {
      type: 'HOTEL-TO-AIRPORT',
      isHomePickup: true,
      hotel: {
        name: selectedAddress.name,
        googlePlaceId: selectedAddress.placeId,
        kmFromAirport,
        latitude: selectedAddress.latitude,
        longitude: selectedAddress.longitude,
      },
      travellers: {
        ...transfer.travellers,
        // Reset seats if no children
        boosterSeats: transfer.travellers.children ? transfer.travellers.boosterSeats : 0,
        childSeats: transfer.travellers.children ? transfer.travellers.childSeats : 0,
      },
      baggage: transfer.baggage,
      date: selectedDate,
      time: selectedTime || '00:00',
      flightNumber: flightNumber,
      specialRequests: specialRequests || undefined,
      option: firstTransferOption,
      title: homeToAirportData.experienceBookingView.title,
      redemptionLocationId: selectedAirportId,
      airport,
    }

    onSubmit({
      transfer: finalTransfer,
      experience: homeToAirportData.experienceBookingView.originalExperience,
    })
  }, [
    firstTransferOption,
    selectedRedemptionLocation,
    homeToAirportData.experienceBookingView,
    selectedAddress,
    kmFromAirport,
    transfer,
    selectedDate,
    selectedTime,
    flightNumber,
    selectedAirportId,
    intl,
    onSubmit,
  ])

  // Build airport location for the address select component (used for biasing autocomplete)
  const airportForAddressSelect = useMemo(() => {
    if (selectedRedemptionLocation) {
      return {
        code: selectedRedemptionLocation.state ?? '',
        name: selectedRedemptionLocation.city ?? selectedRedemptionLocation.name,
        latitude: selectedRedemptionLocation.latitude,
        longitude: selectedRedemptionLocation.longitude,
      }
    }
    return defaultAirport
  }, [selectedRedemptionLocation, defaultAirport])

  // Build transfer view for child components (travellers/baggage selectors)
  // Note: 'hotel' contains the home address for this form
  const transferViewForComponents = useMemo((): App.ExperienceTransferView => ({
    type: 'HOTEL-TO-AIRPORT',
    hotel: {
      name: selectedAddress.name,
      googlePlaceId: selectedAddress.placeId,
      kmFromAirport,
    },
    travellers: transfer.travellers,
    baggage: transfer.baggage,
  }), [selectedAddress, kmFromAirport, transfer])

  const isLoading = homeToAirportData.fetchingExperience ||
    homeToAirportData.fetchingDates ||
    homeToAirportData.fetchingTransferOptions
  const homeToAirportLoadErrorMessage = !isLoading && redemptionLocations.length === 0
    ? intl.formatMessage({
        id: 'H0p2SWdqFGof93qSktFG',
        defaultMessage: 'We couldn’t load any eligible airports for this Home to Airport transfer right now. Please try again later.',
        description: 'Critical banner shown in the home-to-airport transfer form when no eligible airports can be displayed, so the form cannot be completed.',
      })
    : undefined

  return (
    <VerticalSpacer gap={24} as="form" onSubmit={handleFormSubmit} id={formId}>
      {/* Hidden inputs for form submission - use HOTEL-TO-AIRPORT for backend compatibility */}
      <input type="hidden" name="transfer.type" value="HOTEL-TO-AIRPORT" />

      {/* Single loading state: show one message by priority so we never show two loaders */}
      {isLoading && (
        <LoadingIndicator inline visible>
          {homeToAirportData.fetchingExperience && (
            <FormattedMessage
              id="MoExhUgu5iFluZ4MR0ss"
              defaultMessage="Loading airports..."
              description="Loading indicator text while fetching available airports for home-to-airport transfer"
            />
          )}
          {!homeToAirportData.fetchingExperience && homeToAirportData.fetchingDates && (
            <FormattedMessage
              id="vBmm_8VSOFXNixK-o9Mi"
              defaultMessage="Loading available dates..."
              description="Loading indicator text while fetching available dates for the selected airport transfer"
            />
          )}
          {!homeToAirportData.fetchingExperience && !homeToAirportData.fetchingDates && homeToAirportData.fetchingTransferOptions && (
            <FormattedMessage
              id="g0rXTMZFCfvUOnngGPSM"
              defaultMessage="Loading transfer options..."
              description="Loading indicator text while fetching transfer options for the selected airport and date"
            />
          )}
        </LoadingIndicator>
      )}

      {homeToAirportLoadErrorMessage && (
        <MessageBanner kind="critical">
          {homeToAirportLoadErrorMessage}
        </MessageBanner>
      )}

      {/* All form inputs - visible when airports are loaded */}
      {redemptionLocations.length > 0 && (
        <>
          <FormGrid>
            <AddressInput
              label={intl.formatMessage({
                id: 'Xowqdd59IcO0H69zctz1',
                defaultMessage: 'Home address',
                description: 'Label for the home address input field in the home-to-airport transfer form',
              })}
              required
              placeholder={intl.formatMessage({
                id: 'ug7bhRTAu1IQq1Cij9VH',
                defaultMessage: 'Enter your address',
                description: 'Placeholder text for the home address autocomplete input in transfer form',
              })}
              onChange={handleAddressChange}
              requiredErrorMessage={intl.formatMessage({
                id: 'NVm7GoENHa3I7sQkKtxo',
                defaultMessage: 'Please enter your home address',
                description: 'Validation error message when home address is not provided in transfer form',
              })}
              originLatitude={airportForAddressSelect?.latitude}
              originLongitude={airportForAddressSelect?.longitude}
              defaultValue={selectedAddress.name}
              defaultPlaceId={selectedAddress.placeId}
            />

            <Select
              label={intl.formatMessage({
                id: 'ofLo16Tyq1WTbex8nwC2',
                defaultMessage: 'Destination airport',
                description: 'Label for the destination airport dropdown in the home-to-airport transfer form',
              })}
              value={selectedAirportId}
              onChange={handleAirportChange}
              required
            >
              <option value="" disabled>
                {intl.formatMessage({
                  id: 'HmDTPx4wQJVdAjAgMo7a',
                  defaultMessage: 'Select an airport',
                  description: 'Default disabled option text in the airport dropdown for home-to-airport transfer',
                })}
              </option>
              {redemptionLocations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.city ?? location.name}
                </option>
              ))}
            </Select>
          </FormGrid>

          <StretchedGroup tabletDirection="horizontal" direction="vertical" gap={12}>
            <DatePickerInput
              key={selectedAirportId}
              label={intl.formatMessage({
                id: 'Vlbprc04nzr0-tVAy12T',
                defaultMessage: 'Departure date',
                description: 'Label for the departure date picker in the home-to-airport transfer form',
              })}
              required
              min={minDate}
              defaultValue={selectedDate}
              name="transfer.date"
              requiredErrorMessage={intl.formatMessage({
                id: 'JJMhadkTUylXrUlma31b',
                defaultMessage: 'Please select a date',
                description: 'Validation error message when departure date is not selected in transfer form',
              })}
              onDateChange={handleDateChange}
              disabled={!selectedRedemptionLocation || homeToAirportData.fetchingDates}
              dayRender={availableDateSet.size > 0 ? dayRender : undefined}
            />

            <TimeInput
              label={intl.formatMessage({
                id: 'TJ5PMKjq8NmWRbKNNETz',
                defaultMessage: 'Flight departure time',
                description: 'Label for the flight departure time input in the home-to-airport transfer form',
              })}
              defaultValue={selectedTime}
              name="transfer.time"
              placeholder={intl.formatMessage({
                id: 'zgq2D_S4YgN6RB8woTQ6',
                defaultMessage: 'Select time',
                description: 'Placeholder text for the flight departure time input in the transfer form',
              })}
              step={15}
              requiredErrorMessage={intl.formatMessage({
                id: 'Gm4BxFpL19JCAzOn-pqc',
                defaultMessage: 'Please select departure time',
                description: 'Validation error message when departure time is not selected in transfer form',
              })}
              onChange={handleTimeChange}
            />

            <TextInput
              label={intl.formatMessage({
                id: 'CbD-nWQRuUtw3RP4xpd6',
                defaultMessage: 'Flight number',
                description: 'Label for the flight number text input in the home-to-airport transfer form',
              })}
              defaultValue={flightNumber}
              name="transfer.flightNumber"
              placeholder={intl.formatMessage({
                id: 'HQSWtjwVoLL_WKJww9qs',
                defaultMessage: 'e.g. QF123',
                description: 'Placeholder example for flight number input in home to airport transfer form',
              })}
              onChange={handleFlightNumberChange}
            />
          </StretchedGroup>

          <ExperienceDetailsTransferBookingTravellers
            name="transfer"
            options={[]}
            transfer={transferViewForComponents}
            onChange={handleTravellersChange}
            maxPassengersOverride={6}
          />

          <ExperienceDetailsTransferBookingBaggage
            name="transfer"
            options={[]}
            transfer={transferViewForComponents}
            onChange={handleBaggageChange}
            maxBaggageOverride={4}
          />
        </>
      )}

      {hasTicketValues && !distanceExceedsMax && coreInputsFilled && !isLoading && totalExtraCharge > 0 && (
        <TransferSurchargeSummary
          kmFromAirport={kmFromAirport}
          hasExtraDistanceCharge={hasExtraDistanceCharge}
          extraDistanceCharge={extraDistanceCharge}
          hasCarSeatCharge={hasCarSeatCharge}
          needsVehicleUpgrade={needsVehicleUpgrade}
          totalExtraCharge={totalExtraCharge}
        />
      )}

      {hasTicketValues && !distanceExceedsMax && coreInputsFilled && !isLoading && (
        <HiddenInput
          required={!firstTransferOption}
          invalidMessage={intl.formatMessage({
            id: 'rWdjZZBOISe4UWUcgGmV',
            defaultMessage: 'Please adjust your search parameters and try again.',
            description: 'Error message when no transfer options are available for the selected parameters',
          })}
        />
      )}

      {hasTicketValues && distanceExceedsMax && (
        <BodyText variant="medium" colour="critical">
          <FormattedMessage
            id="38ZgqYt3OtXwxjI0AUTG"
            defaultMessage="Sorry, transfers are not available for distances over {maxDistance}km from the airport. Your selected address is {distance}km away."
            description="Error message when the selected address exceeds the maximum transfer distance from airport"
            values={{
              maxDistance: MAX_DISTANCE_KM,
              distance: kmFromAirport,
            }}
          />
        </BodyText>
      )}

      {distanceCalculationError && selectedAddress.name && !kmFromAirport && (
        <BodyText variant="medium" colour="critical">
          <FormattedMessage
            id="2Xu8__1MRSYi7SagBqb6"
            description="Error message when the distance calculation to the airport fails for the selected address"
            defaultMessage="We couldn’t calculate the distance to the airport. Please try selecting your address again."
          />
        </BodyText>
      )}
    </VerticalSpacer>
  )
}

export default HomeToAirportTransferForm
